// Types
import { Branch } from "@/types/supabase";

// Libs
import { useState, useEffect, useCallback } from "react";
import {
  useParams,
  useSearchParams,
  Link,
  useNavigate,
} from "react-router-dom";
import { toastHandler } from "@/lib/ToastHandler";

// Hooks
import { useBrandsApi } from "@/hooks/useBrandsApi";
import { useBranchesApi } from "@/hooks/useBranchesApi";
import useDebounce from "@/hooks/useDebounce";
import { usePermissions } from "@/contexts/PermissionContext";
import { useAuth } from "@/hooks/useAuth";

// Components
import { Card } from "@/components/atoms/card";
import { Button } from "@/components/atoms/button";
import LoadingSpinner from "@/components/orders/LoadingSpinner";
import ErrorMessage from "@/components/orders/ErrorMessage";
import { Input } from "@/components/atoms/input";
import Pagination from "@/components/atoms/Pagination";
import { EmptyState } from "@/components/atoms/empty-state";

// Icons
import {
  CheckCircle2,
  ChevronRight,
  Loader2,
  Pencil,
  Plus,
  Search,
  Store,
  XCircle,
  Info,
} from "lucide-react";
import { BusinessAccessDenied } from "@/components/access-control/AccessDenied";
import {
  Select,
  SelectItem,
  SelectContent,
  SelectValue,
  SelectTrigger,
} from "@/components/atoms/select";
import { CountryBadge } from "../Brand";
import { useAuth0 } from "@auth0/auth0-react";

export default function BrandDetail() {
  const { id } = useParams<{ id: string }>();
  const { error, updateBrand, fetchBrandById } = useBrandsApi();
  const {
    error: branchesError,
    fetchBranchesPaginated,
    handleToggleBranchStatus,
  } = useBranchesApi();
  const navigate = useNavigate();
  const { hasPermission, hasBrandAccess } = usePermissions();
  const { userRole } = useAuth();
  const { user } = useAuth0();
  // Access control: Check if user has permission to view this brand
  const canViewBrands = hasPermission("business:read");
  const hasAccessToBrand = id ? hasBrandAccess(Number(id)) : false;

  // For managers, check if they have access to this specific brand
  const shouldShowAccessDenied =
    !canViewBrands || (userRole === "manager" && !hasAccessToBrand);

  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState(id);
  const [brandName, setBrandName] = useState("");
  const [brandCountry, setBrandCountry] = useState<{
    name: string;
    short_name: string;
    flag_url: string;
  } | null>(null);
  const [groupingDistanceMeters, setGroupingDistanceMeters] =
    useState<string>("100");
  const [saving, setSaving] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [editMode, setEditMode] = useState(false);
  const [stateFilter, setStateFilter] = useState("all");

  // State to store original values for cancel functionality
  const [originalBrandName, setOriginalBrandName] = useState("");
  const [originalGroupingDistanceMeters, setOriginalGroupingDistanceMeters] =
    useState<string>("none");

  const [loadingBranches, setLoadingBranches] = useState(false);
  const [searchBranches, setSearchBranches] = useState("");
  const debouncedSearch = useDebounce(searchBranches, 500);
  const [brandBranches, setBrandBranches] = useState<Branch[]>([]);
  const [branchesTotalCount, setBranchesTotalCount] = useState(0);
  const [branchesTotalPages, setBranchesTotalPages] = useState(0);
  const [branchesCurrentPage, setBranchesCurrentPage] = useState(1);
  const [branchedLimit] = useState(10);
  const { isManager } = useAuth();
  // Load initial brand data
  useEffect(() => {
    const fetchBrand = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const brand = await fetchBrandById(Number(id));
        const brandBranches = await fetchBranchesPaginated({
          page: 1,
          limit: branchedLimit,
          brandId: Number(id),
          user: isManager ? user : undefined,
        });

        setGroupingDistanceMeters(
          brand?.grouping_distance_meters?.toString() || "none",
        );
        setBrandBranches(brandBranches.data);
        setBranchesTotalCount(brandBranches.total);
        setBranchesTotalPages(brandBranches.totalPages);
        if (brand) {
          setBrandName(brand.name);
          setCode(brand.id.toString());
          // Store original values for cancel functionality
          setOriginalBrandName(brand.name);
          setOriginalGroupingDistanceMeters(
            brand?.grouping_distance_meters?.toString() || "none",
          );
          setGroupingDistanceMeters(
            brand?.grouping_distance_meters?.toString() || "none",
          );
          setBrandCountry(brand.country);
        }
      } catch (error) {
        console.error("Error fetching brand:", error);
        navigate("/brands");
      } finally {
        setLoading(false);
      }
    };
    fetchBrand();
  }, []);

  useEffect(() => {
    if (searchParams.get("edit")) {
      setEditMode(true);
      // Remove the param from the URL
      searchParams.delete("edit");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Fetch branches for a given page and filters
  const fetchBranches = useCallback(
    async (page: number, search: string, state: string) => {
      if (!id) return;
      setLoadingBranches(true);
      try {
        const result = await fetchBranchesPaginated({
          page,
          limit: branchedLimit,
          brandId: id,
          search: search || undefined,
          stateFilter: state !== "all" ? state : undefined,
        });
        setBrandBranches(result.data);
        setBranchesTotalCount(result.total);
        setBranchesTotalPages(result.totalPages);
      } catch (error) {
        console.error("Error fetching branches:", error);
      } finally {
        setLoadingBranches(false);
      }
    },
    [id, branchedLimit],
  );

  // When search or filter changes, always fetch page 1 and reset page state
  useEffect(() => {
    setBranchesCurrentPage(1);
  }, [debouncedSearch, stateFilter, id, user]);

  useEffect(() => {
    fetchBranches(1, debouncedSearch, stateFilter);
  }, [debouncedSearch, stateFilter, id, user]);

  // When page changes (but not search/filter), fetch that page
  useEffect(() => {
    // Don't double-fetch on initial mount or when search/filter changes (handled above)
    fetchBranches(branchesCurrentPage, debouncedSearch, stateFilter);
  }, [branchesCurrentPage]);

  const handleSaveChanges = async () => {
    if (!id || !brandName.trim()) return;

    if (groupingDistanceMeters === "none") {
      toastHandler({
        title: "Error",
        description: "Debe elegir una distancia de agrupación de órdenes.",
        variant: "destructive",
      });
      return;
    }
    setSaving(true);
    try {
      await updateBrand(Number(id), {
        name: brandName.trim(),
        grouping_distance_meters: Number(groupingDistanceMeters),
      });

      // Refresh brand data to ensure UI shows the latest information
      const updatedBrand = await fetchBrandById(Number(id));
      if (updatedBrand) {
        setBrandName(updatedBrand.name);
        setCode(updatedBrand.id.toString());
        setGroupingDistanceMeters(
          updatedBrand?.grouping_distance_meters?.toString() || "none",
        );
        // Update original values after successful save
        setOriginalBrandName(updatedBrand.name);
        setOriginalGroupingDistanceMeters(
          updatedBrand?.grouping_distance_meters?.toString() || "none",
        );
      }

      toastHandler({
        title: "Éxito",
        description: "Marca actualizada correctamente.",
        variant: "success",
      });

      setEditMode(false);
    } catch (error: any) {
      toastHandler({
        title: "Error",
        description:
          error.response?.data?.message || "No se pudo actualizar la marca.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleViewBranchDetails = (branchId: number) => {
    navigate(`/brands/${id}/branches/${branchId}`);
  };

  const handleEditMode = () => {
    // Store current values before entering edit mode
    setOriginalBrandName(brandName);
    setOriginalGroupingDistanceMeters(groupingDistanceMeters);
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    // Restore original values
    setBrandName(originalBrandName);
    setGroupingDistanceMeters(originalGroupingDistanceMeters);
    setCode(id);
    setEditMode(false);
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  // Access control: Show access denied if user doesn't have permission
  if (shouldShowAccessDenied) {
    return <BusinessAccessDenied />;
  }

  return (
    <div className="min-h-screen overflow-y-auto bg-gray-50 w-full">
      {/* Header - Enhanced responsiveness */}
      <header className="bg-white px-4 sm:px-6 py-4 sm:py-6 w-full shadow-sm">
        <div className="max-w-7xl mx-auto">
          {/* Mobile-first approach: allow wrapping on very small screens */}
          <h1 className="text-lg leading-tight sm:text-xl md:text-2xl font-semibold text-gray-900 break-words sm:truncate">
            <span className="flex items-center gap-2">
              {brandName &&
                brandName.charAt(0).toUpperCase() + brandName.slice(1)}
              {brandCountry && <CountryBadge country={brandCountry} />}
            </span>
          </h1>
          {/* Improved breadcrumbs for mobile */}
          <nav className="flex items-center gap-1 text-sm text-gray-600 mt-2 overflow-x-auto">
            <Link
              to="/brands"
              className="text-gray-500 hover:text-indigo-600 transition-colors whitespace-nowrap"
            >
              Negocios
            </Link>
            <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
            {/* Allow brand name to wrap on very small screens */}
            <span className="font-medium text-gray-900 break-words sm:truncate">
              {brandName &&
                brandName.charAt(0).toUpperCase() + brandName.slice(1)}
            </span>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Information Section Header */}
        <Card className="bg-white shadow-sm border border-gray-200">
          <div className="p-4 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                <Info className="w-5 h-5 text-gray-600" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="font-semibold text-gray-900">Información</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Información y datos sobre la marca
                </p>
              </div>
            </div>

            {/* Action buttons - responsive layout */}
            {editMode ? (
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full sm:w-auto text-gray-600 hover:text-gray-900 hover:bg-gray-100 border border-gray-300"
                  onClick={handleCancelEdit}
                >
                  Cancelar
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  className="w-full sm:w-auto bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleSaveChanges}
                  disabled={saving || !brandName.trim()}
                >
                  <div className="flex items-center gap-2 justify-center">
                    {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                    <span>{saving ? "Guardando..." : "Guardar cambios"}</span>
                  </div>
                </Button>
              </div>
            ) : (
              <Button
                size="sm"
                variant="default"
                onClick={handleEditMode}
                className="w-full sm:w-auto bg-indigo-600 text-white hover:bg-indigo-700"
              >
                <div className="flex items-center gap-2 justify-center">
                  <Pencil className="w-4 h-4" />
                  <span>Editar</span>
                </div>
              </Button>
            )}
          </div>
        </Card>

        {/* Information Form - Responsive grid */}
        <Card className="bg-white shadow-sm border border-gray-200">
          <div className="p-4 sm:p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  ID
                </label>
                <div className="text-sm p-2 text-gray-600 font-medium border rounded-lg bg-gray-50 pointer-events-none">
                  #{code}
                </div>
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Nombre
                </label>
                <Input
                  type="text"
                  className="bg-white border border-gray-300 text-gray-900 text-base rounded-lg block disabled:bg-gray-50 disabled:text-gray-600"
                  placeholder="Nombre del negocio"
                  value={brandName}
                  onChange={(e) => setBrandName(e.target.value)}
                  disabled={!editMode}
                />
              </div>
              <div className="space-y-2 ">
                <label className="block text-sm font-medium text-gray-700">
                  Distancia de agrupación de órdenes
                </label>
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:items-center">
                  <div className="flex-1">
                    <Select
                      value={groupingDistanceMeters.toString()}
                      onValueChange={(val) => setGroupingDistanceMeters(val)}
                      disabled={!editMode}
                    >
                      <SelectTrigger className="bg-white border border-gray-300 text-gray-900 text-base rounded-lg disabled:bg-gray-50 disabled:text-gray-600">
                        <SelectValue placeholder="Seleccionar distancia" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Elegir distancia</SelectItem>
                        <SelectItem value="50">50 mts</SelectItem>
                        <SelectItem value="100">100 mts</SelectItem>
                        <SelectItem value="200">200 mts</SelectItem>
                        <SelectItem value="300">300 mts</SelectItem>
                        <SelectItem value="500">500 mts</SelectItem>
                        <SelectItem value="1000">1 km</SelectItem>
                        <SelectItem value="2000">2 km</SelectItem>
                        <SelectItem value="5000">5 km</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Divider */}
        <div className="border-t border-gray-200" />

        {/* Branches Section Header */}
        <Card className="bg-white shadow-sm border border-gray-200">
          <div className="p-4 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                <Store className="w-5 h-5 text-gray-600" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="font-semibold text-gray-900">Sucursales</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Gestiona y crea nuevas sucursales
                </p>
              </div>
            </div>
            <Button
              onClick={() => navigate(`/brands/${id}/branches/new`)}
              className="w-full sm:w-auto px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" />
              <span>Agregar nueva</span>
            </Button>
          </div>
        </Card>

        {/* Branches Content */}
        <Card className="bg-white shadow-sm border border-gray-200 overflow-hidden">
          {/* Search and filter controls - Enhanced mobile layout */}
          <div className="p-4 sm:p-6 border-b border-gray-200">
            <div className="flex flex-col space-y-4 lg:flex-row lg:items-center lg:justify-between lg:space-y-0">
              <div className="flex-1 max-w-md">
                <Input
                  type="text"
                  placeholder="Buscar por nombre, dirección o coordenadas..."
                  value={searchBranches}
                  onChange={(e) => setSearchBranches(e.target.value)}
                  className="w-full h-10"
                  icon={<Search className="w-4 h-4 text-gray-400" />}
                />
              </div>
              <div className="w-full lg:w-auto">
                <Select value={stateFilter} onValueChange={setStateFilter}>
                  <SelectTrigger className="w-full lg:w-auto min-w-[180px]">
                    <SelectValue placeholder="Filtrar por estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los estados</SelectItem>
                    <SelectItem value="active">Activo</SelectItem>
                    <SelectItem value="inactive">Inactivo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Branches Content Area */}
          <div className="min-h-[400px]">
            {loadingBranches ? (
              <div className="p-6 text-center flex flex-col items-center justify-center h-64">
                <Loader2 className="w-8 h-8 mb-3 animate-spin text-indigo-600" />
                <span className="text-gray-600">Cargando sucursales...</span>
              </div>
            ) : branchesError ? (
              <div className="p-6 text-center text-red-500">
                {branchesError}
              </div>
            ) : branchesTotalCount === 0 ? (
              <div className="p-6">
                {searchBranches !== "" || stateFilter !== "all" ? (
                  <EmptyState
                    title="No se encontraron resultados"
                    description="No se encontraron resultados para el criterio ingresado"
                    illustration={{
                      src: "/branch-illustration/empty-branch-illustration.svg",
                      alt: "No se encontraron resultados",
                    }}
                  />
                ) : (
                  <EmptyState
                    title="Sin sucursales asignadas"
                    description="No hay sucursales asignadas a esta marca"
                    illustration={{
                      src: "/branch-illustration/empty-branch-illustration.svg",
                      alt: "Sin sucursales asignadas",
                    }}
                  />
                )}
              </div>
            ) : (
              <>
                {/* Desktop view - Table */}
                <div className="hidden xl:block overflow-x-auto">
                  <table className="w-full text-sm text-left text-gray-500">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th scope="col" className="px-6 py-4 font-medium">
                          Nombre
                        </th>
                        <th scope="col" className="px-6 py-4 font-medium">
                          Dirección
                        </th>
                        <th scope="col" className="px-6 py-4 font-medium">
                          Coordenadas
                        </th>
                        <th scope="col" className="px-6 py-4 font-medium">
                          Agrupación de pedidos
                        </th>
                        <th scope="col" className="px-6 py-4 font-medium">
                          Estado
                        </th>
                        <th scope="col" className="px-6 py-4 font-medium">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {brandBranches.map((branch: Branch) => (
                        <tr
                          key={branch.id}
                          className="bg-white hover:bg-gray-50 cursor-pointer transition-colors"
                          onClick={() => handleViewBranchDetails(branch.id)}
                        >
                          <td className="px-6 py-4 font-medium text-gray-900">
                            {branch.name}
                          </td>
                          <td className="px-6 py-4 text-gray-700 max-w-xs truncate">
                            {branch.full_address}
                          </td>
                          <td className="px-6 py-4 text-gray-700 font-mono text-xs">
                            {branch.location_lat?.toFixed(6)},{" "}
                            {branch.location_lon?.toFixed(6)}
                          </td>
                          <td className="px-6 py-4">
                            {branch.order_grouping_enabled ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                Habilitado
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                Deshabilitado
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            {branch.is_active ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Activo
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                Inactivo
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(
                                    `/brands/${id}/branches/${branch.id}?edit=1`,
                                  );
                                }}
                                className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md p-2 transition-colors"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleBranchStatus(branch.id);
                                  setBrandBranches(
                                    brandBranches.map((b) =>
                                      b.id === branch.id
                                        ? { ...b, is_active: !branch.is_active }
                                        : b,
                                    ),
                                  );
                                }}
                                className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md p-2 transition-colors"
                              >
                                {branch.is_active ? (
                                  <XCircle className="w-4 h-4" />
                                ) : (
                                  <CheckCircle2 className="w-4 h-4" />
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile/Tablet view - Enhanced responsive cards */}
                <div className="xl:hidden">
                  <div className="p-2 sm:p-4 space-y-3 sm:space-y-4">
                    {brandBranches.map((branch: Branch) => (
                      <Card
                        key={branch.id}
                        className="bg-white border border-gray-200 shadow-sm hover:shadow-md cursor-pointer transition-shadow p-3 sm:p-4"
                        onClick={() => handleViewBranchDetails(branch.id)}
                      >
                        <div className="flex justify-between items-start mb-3">
                          <div className="min-w-0 flex-1 pr-2">
                            {/* Branch name with responsive sizing */}
                            <h3 className="font-semibold text-gray-900 text-sm sm:text-base leading-tight">
                              {branch.name}
                            </h3>
                            {/* Status badges with responsive layout */}
                            <div className="flex flex-col xs:flex-row gap-1.5 xs:gap-2 mt-2">
                              {branch.is_active ? (
                                <span className="inline-flex items-center w-fit px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  Activo
                                </span>
                              ) : (
                                <span className="inline-flex items-center w-fit px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  Inactivo
                                </span>
                              )}
                              {branch.order_grouping_enabled ? (
                                <span className="inline-flex items-center w-fit px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  <span className="hidden xs:inline">
                                    Agrupación habilitada
                                  </span>
                                  <span className="xs:hidden">
                                    Agrupación ON
                                  </span>
                                </span>
                              ) : (
                                <span className="inline-flex items-center w-fit px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                  <span className="hidden xs:inline">
                                    Agrupación deshabilitada
                                  </span>
                                  <span className="xs:hidden">
                                    Agrupación OFF
                                  </span>
                                </span>
                              )}
                            </div>
                          </div>
                          {/* Action buttons with improved spacing */}
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(
                                  `/brands/${id}/branches/${branch.id}?edit=1`,
                                );
                              }}
                              className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg p-2 transition-colors"
                              title="Editar sucursal"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleBranchStatus(branch.id);
                                setBrandBranches(
                                  brandBranches.map((b) =>
                                    b.id === branch.id
                                      ? { ...b, is_active: !branch.is_active }
                                      : b,
                                  ),
                                );
                              }}
                              className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg p-2 transition-colors"
                              title={
                                branch.is_active
                                  ? "Desactivar sucursal"
                                  : "Activar sucursal"
                              }
                            >
                              {branch.is_active ? (
                                <XCircle className="w-4 h-4" />
                              ) : (
                                <CheckCircle2 className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </div>

                        {/* Branch details with improved responsive design */}
                        <div className="space-y-2.5 sm:space-y-3 text-xs sm:text-sm">
                          <div>
                            <span className="text-gray-500 font-medium block">
                              Dirección:
                            </span>
                            <p className="text-gray-700 mt-0.5 break-words leading-relaxed">
                              {branch.full_address}
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-500 font-medium block">
                              Coordenadas:
                            </span>
                            <p className="text-gray-700 mt-0.5 font-mono text-xs break-all">
                              {branch.location_lat?.toFixed(6)},{" "}
                              {branch.location_lon?.toFixed(6)}
                            </p>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>

                {/* Pagination */}
                <div className="px-4 sm:px-6 py-4 border-t border-gray-200">
                  <Pagination
                    totalPages={branchesTotalPages}
                    currentPage={branchesCurrentPage}
                    onPageChange={setBranchesCurrentPage}
                  />
                  {branchesTotalCount > 0 && (
                    <div className="text-sm text-gray-500 mt-3 text-center sm:text-left">
                      Mostrando página {branchesCurrentPage} de{" "}
                      {branchesTotalPages} ({branchesTotalCount} sucursales en
                      total)
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
